import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphQLClient } from "../graphqlClient";
import { GET_EARNED_BADGES, GET_CMS_BADGES } from "../api/queries";
import { useProductFeatures } from "./useProductFeatures";
import { useReactQueryErrorReporter } from "./use-error-reporter";

export interface CMSBadge {
  id: string;
  name: string;
  description?: unknown;
  displayOrder?: number | null;
  tooltip?: string | null;
  type: string;
  subtype?: string | null;
  productPlans?: string[] | null;
  icon?: { url?: string | null; alt?: string | null } | null;
}

export interface EarnedBadge {
  id: string;
  earned: boolean;
  progress: number;
  earnedAt?: string | null;
  acknowledged: boolean;
  acknowledgedAt?: string | null;
  earningEvents: Array<{
    id: string;
    eventType: string;
    timestamp: string;
    data?: string | null;
  }>;
}

export interface BadgeView {
  id: string;
  name: string;
  description?: unknown;
  displayOrder?: number | null;
  tooltip?: string | null;
  iconUrl?: string | null;
  iconAlt?: string | null;
  type: string;
  subtype?: string | null;
  earned: boolean;
  earnedAt?: string | null;
  acknowledged: boolean;
  earningEvents: EarnedBadge["earningEvents"];
  progress: number; // 0..1, computed by the badges-processor lambda
}

interface GetCMSBadgesResponse {
  cmsBadges: CMSBadge[];
}

interface GetEarnedBadgesResponse {
  getEarnedBadges: {
    earnedBadges: EarnedBadge[];
  };
}

export const useBadges = () => {
  const { productPlans, isLoading: plansLoading } = useProductFeatures();

  const cmsQuery = useQuery<GetCMSBadgesResponse>({
    queryKey: ["cmsBadges"],
    queryFn: () => graphQLClient.request(GET_CMS_BADGES),
    staleTime: 1000 * 60 * 10,
  });

  // The cache is invalidated by `useNewBadgeCheck` after a `checkForNew: true`
  // server pass, so we don't need recentEvents in the key here — that would
  // refetch on every reported event without changing the result.
  const earnedQuery = useQuery<GetEarnedBadgesResponse>({
    queryKey: ["getEarnedBadges", productPlans],
    queryFn: () =>
      graphQLClient.request(GET_EARNED_BADGES, {
        recentEvents: [],
        productPlans: productPlans || [],
        checkForNew: false,
      }),
    enabled: !plansLoading,
    staleTime: 1000 * 30,
  });

  useReactQueryErrorReporter(
    "Could not load the badge catalog",
    cmsQuery.error,
    false,
  );
  useReactQueryErrorReporter(
    "Could not load earned badges",
    earnedQuery.error,
    false,
  );

  const badges = useMemo<BadgeView[]>(() => {
    const catalog = cmsQuery.data?.cmsBadges || [];
    const earned = earnedQuery.data?.getEarnedBadges?.earnedBadges || [];
    const earnedById = new Map(earned.map((b) => [String(b.id), b]));

    const planSet = new Set(productPlans);
    const visible = catalog.filter((badge) => {
      const codes = badge.productPlans || [];
      if (codes.length === 0) return false;
      return codes.some((code) => planSet.has(code));
    });

    return visible.map((badge) => {
      const earnedRow = earnedById.get(String(badge.id));
      const isEarned = !!earnedRow?.earned;
      return {
        id: String(badge.id),
        name: badge.name,
        description: badge.description,
        displayOrder: badge.displayOrder ?? null,
        tooltip: badge.tooltip ?? null,
        iconUrl: badge.icon?.url ?? null,
        iconAlt: badge.icon?.alt ?? badge.name,
        type: badge.type,
        subtype: badge.subtype ?? null,
        earned: isEarned,
        earnedAt: earnedRow?.earnedAt ?? null,
        acknowledged: !!earnedRow?.acknowledged,
        earningEvents: earnedRow?.earningEvents ?? [],
        progress: earnedRow?.progress ?? 0,
      };
    });
  }, [cmsQuery.data, earnedQuery.data, productPlans]);

  const sections = useMemo(() => {
    const xp: BadgeView[] = [];
    const buildingBlocks: BadgeView[] = [];
    const personality: BadgeView[] = [];
    const exploration: BadgeView[] = [];

    for (const badge of badges) {
      const subtype = (badge.subtype || "").toLowerCase();
      const type = (badge.type || "").toLowerCase();
      if (subtype === "xp") {
        xp.push(badge);
      } else if (subtype === "building blocks") {
        buildingBlocks.push(badge);
      } else if (type === "personality") {
        personality.push(badge);
      } else if (type === "exploration") {
        exploration.push(badge);
      }
    }

    const byDisplayOrder = (a: BadgeView, b: BadgeView) => {
      const aOrder = a.displayOrder ?? Infinity;
      const bOrder = b.displayOrder ?? Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    };

    return {
      xp: [...xp].sort(byDisplayOrder),
      buildingBlocks: [...buildingBlocks].sort(byDisplayOrder),
      personality: [...personality].sort(byDisplayOrder),
      exploration: [...exploration].sort(byDisplayOrder),
    };
  }, [badges]);

  return {
    badges,
    sections,
    loading: cmsQuery.isLoading || earnedQuery.isLoading || plansLoading,
    error: cmsQuery.error || earnedQuery.error,
  };
};
